/**
 * MarkReturnedDialog
 * Quick action for seafarers to finalise an open-ended trip by entering
 * the return date. Calls POST /api/trips/{trip_id}/mark-returned.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const todayIso = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const MarkReturnedDialog = ({ trip, open, onOpenChange, onCompleted }) => {
  const [returnDate, setReturnDate] = useState(todayIso());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setReturnDate(todayIso());
  }, [open]);

  const handleSubmit = async () => {
    if (!returnDate) {
      toast.error('Please select a return date');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/trips/${trip.id}/mark-returned`, {
        date_returned_uk: returnDate,
      });
      toast.success('Trip marked as returned');
      onOpenChange(false);
      if (onCompleted) onCompleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as returned');
    } finally {
      setSubmitting(false);
    }
  };

  if (!trip) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm" data-testid="mark-returned-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <CheckCircle2 className="h-4 w-4 text-sky-400" />
            Mark trip as returned
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            {trip.vessel_name || trip.hotel_name || trip.course_name || 'Open-ended trip'}
            {trip.date_left_uk && <> · Departed {trip.date_left_uk}</>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="return-date" className="text-xs text-slate-400">Return date</Label>
          <Input
            id="return-date"
            type="date"
            value={returnDate}
            min={trip.date_left_uk || undefined}
            max={todayIso()}
            onChange={(e) => setReturnDate(e.target.value)}
            className="bg-slate-900 border-slate-600 text-white h-9"
            data-testid="mark-returned-date-input"
          />
          <p className="text-[10px] text-slate-500">
            Must be on or after the departure date. Future dates aren't allowed — use Edit for that.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="border-slate-600 text-slate-300"
            data-testid="mark-returned-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !returnDate}
            className="bg-sky-600 hover:bg-sky-700"
            data-testid="mark-returned-submit"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
            Confirm return
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarkReturnedDialog;
